/**
 * Ciclador día → tarde → noche para previsualizar el crossfade. Dos
 * fuentes de activación:
 *
 *  1. URL `?cycle` (opcional `?cycle=N` con N en segundos) — útil para
 *     pruebas rápidas sin tocar la preferencia persistida. Pinta un
 *     badge discreto abajo a la derecha.
 *
 *  2. Preferencia CRT `51esc.crt.timeOfDay = cycle-10|cycle-30|cycle-60`
 *     desde CONFIG → MODO DÍA/TARDE/NOCHE → CICLO Xs. Persiste.
 *
 * El ciclador parte del estado que dejó el inline script en `<html>`
 * (auto → hora del sistema, salvo URL `?time=`). Cada tick avanza al
 * siguiente modo en orden [day → dusk → night → day].
 *
 * Coordinación: `scene/time-of-day.ts` (watcher horario) detecta
 * cualquiera de las dos activaciones y se desactiva — el ciclador
 * gobierna el `data-time-of-day` mientras esté activo.
 */

type TimeOfDay = 'day' | 'dusk' | 'night';

const ORDER: TimeOfDay[] = ['day', 'dusk', 'night'];
const CYCLE_PREF_RE = /^cycle-(\d+)$/;

export function startTimeOfDayCycle(): void {
  const resolved = resolvePeriod();
  if (resolved === null) return;
  const { periodMs, showBanner } = resolved;

  const root = document.documentElement;
  const initial = root.dataset.timeOfDay as TimeOfDay | undefined;
  let i = Math.max(0, ORDER.indexOf(initial ?? 'day'));

  const apply = () => {
    i = (i + 1) % ORDER.length;
    root.dataset.timeOfDay = ORDER[i] ?? 'day';
  };

  // El primer tick avanza — el usuario ya está viendo el estado inicial
  // que dejó el inline; el cambio real lo notará al cumplirse periodMs.
  window.setInterval(apply, periodMs);

  if (showBanner) {
    const tag = document.createElement('div');
    tag.textContent = `⏱ cycle ${periodMs / 1000}s`;
    tag.style.cssText =
      'position:fixed;bottom:8px;right:8px;padding:4px 8px;background:rgba(0,0,0,.6);color:#9f9;font:11px/1 monospace;border:1px solid #4a4;border-radius:2px;z-index:9999;pointer-events:none;';
    document.body.appendChild(tag);
  }
}

/**
 * Resuelve el período del ciclo. URL gana sobre localStorage. Devuelve
 * `null` si no hay activación.
 */
function resolvePeriod(): { periodMs: number; showBanner: boolean } | null {
  const params = new URLSearchParams(window.location.search);
  if (params.has('cycle')) {
    const raw = params.get('cycle');
    const n = raw && Number.isFinite(Number(raw)) && Number(raw) > 0 ? Number(raw) : 30;
    return { periodMs: n * 1000, showBanner: true };
  }
  const pref = rawTimeOfDayPref();
  const m = CYCLE_PREF_RE.exec(pref);
  if (!m) return null;
  const secs = Number(m[1]);
  if (!Number.isFinite(secs) || secs <= 0) return null;
  return { periodMs: secs * 1000, showBanner: false };
}

/**
 * Lee el campo `timeOfDay` crudo del JSON de CRT. Evita importar
 * `auth/crt-controls.ts` para no arrastrar dependencias de auth en la
 * inicialización de la escena.
 */
function rawTimeOfDayPref(): string {
  try {
    const raw = localStorage.getItem('51esc.crt');
    if (!raw) return 'auto';
    const parsed = JSON.parse(raw) as { timeOfDay?: unknown };
    return typeof parsed.timeOfDay === 'string' ? parsed.timeOfDay : 'auto';
  } catch {
    return 'auto';
  }
}
